"use client";

import { useRef, useEffect, useState } from "react";
import { Maximize, Pause, Play, Volume2 } from "lucide-react";
import { SimpleTooltip } from "@/components/ui/tooltip";

const RESUME_STORAGE_PREFIX = "resource-video-progress:v1";

function parseSavedPlaybackTime(rawValue) {
  if (!rawValue) return null;

  try {
    const parsed = JSON.parse(rawValue);
    if (typeof parsed === "number" && Number.isFinite(parsed) && parsed >= 0) {
      return parsed;
    }

    if (
      typeof parsed?.time === "number" &&
      Number.isFinite(parsed.time) &&
      parsed.time >= 0
    ) {
      return parsed.time;
    }
  } catch {
    const numericValue = Number(rawValue);
    if (Number.isFinite(numericValue) && numericValue >= 0) {
      return numericValue;
    }
  }

  return null;
}

const VideoPlayer = ({
  src,
  className,
  autoPlay = false,
  resumeKey = null,
  debugContext = {},
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const lastSaveTimestampRef = useRef(0);
  const hasRestoredPlaybackRef = useRef(false);
  const playRequestIdRef = useRef(0);
  const pendingPlayPromiseRef = useRef(null);
  const lastRequestedActionRef = useRef(null);
  const isMountedRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [error, setError] = useState(null);
  const storageKey = resumeKey
    ? `${RESUME_STORAGE_PREFIX}:${resumeKey}`
    : null;

  // List of supported video formats and their MIME types
  const supportedFormats = {
    mp4: "video/mp4",
    webm: "video/webm",
    ogg: "video/ogg",
    mov: "video/quicktime",
    avi: "video/x-msvideo",
    m4v: "video/x-m4v",
    "3gp": "video/3gpp",
  };

  // Function to get file extension from src URL
  const getFileExtension = (url) => {
    return url.split(".").pop().toLowerCase();
  };

  // Function to get MIME type based on file extension
  const getMimeType = (url) => {
    const extension = getFileExtension(url);
    return supportedFormats[extension] || null;
  };

  const getSafeSourceForLogs = () => {
    if (!src) return null;

    try {
      const parsed = new URL(src, window.location.origin);
      return `${parsed.origin}${parsed.pathname}`;
    } catch {
      return String(src).split("?")[0];
    }
  };

  const getVideoDebugSnapshot = (extra = {}) => {
    const video = videoRef.current;
    return {
      component: "VideoPlayer",
      source: getSafeSourceForLogs(),
      autoPlay,
      resumeKey,
      debugContext,
      lastRequestedAction: lastRequestedActionRef.current,
      pendingPlay: Boolean(pendingPlayPromiseRef.current),
      playRequestId: playRequestIdRef.current,
      state: {
        isPlaying,
        progress,
        duration,
        volume,
      },
      video: video
        ? {
            paused: video.paused,
            ended: video.ended,
            readyState: video.readyState,
            networkState: video.networkState,
            currentTime: video.currentTime,
            duration: video.duration,
            muted: video.muted,
          }
        : null,
      ...extra,
    };
  };

  const logVideoDebug = (level, message, extra = {}) => {
    const logger = console[level] || console.log;
    logger(`[VideoPlayer] ${message}`, getVideoDebugSnapshot(extra));
  };

  const isInterruptedPlayError = (error) => {
    const name = String(error?.name || "");
    const message = String(error?.message || "");
    return (
      name === "AbortError" ||
      /play\(\) request was interrupted|interrupted by a call to pause|interrupted by a new load request/i.test(
        message
      )
    );
  };

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      playRequestIdRef.current += 1;
      pendingPlayPromiseRef.current = null;
      lastRequestedActionRef.current = "unmount";
      logVideoDebug("debug", "Video player unmounted");
    };
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      const nextDuration = videoRef.current.duration;
      setDuration(Number.isFinite(nextDuration) ? nextDuration : 0);
    }
  }, [videoRef?.current?.duration]);

  useEffect(() => {
    hasRestoredPlaybackRef.current = false;
    lastSaveTimestampRef.current = 0;
    playRequestIdRef.current += 1;
    pendingPlayPromiseRef.current = null;
    lastRequestedActionRef.current = "source-reset";
    setIsPlaying(false);
    setProgress(0);
    setDuration(0);
    setError(null);
    logVideoDebug("debug", "Video source changed; player state reset");
  }, [src, storageKey]);

  const savePlaybackPosition = (timeToSave) => {
    if (!storageKey || typeof window === "undefined") return;

    const numericTime = Number(timeToSave);
    if (!Number.isFinite(numericTime) || numericTime < 0) return;

    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          time: numericTime,
          updatedAt: Date.now(),
        })
      );
    } catch {
      // Ignore storage failures (private mode/quota restrictions)
    }
  };

  const clearSavedPlaybackPosition = () => {
    if (!storageKey || typeof window === "undefined") return;

    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // Ignore storage failures
    }
  };

  const readSavedPlaybackPosition = () => {
    if (!storageKey || typeof window === "undefined") return null;

    try {
      return parseSavedPlaybackTime(window.localStorage.getItem(storageKey));
    } catch {
      return null;
    }
  };

  useEffect(() => {
    return () => {
      if (videoRef.current) {
        savePlaybackPosition(videoRef.current.currentTime);
      }
    };
  }, [storageKey]);

  const requestPause = (reason) => {
    const video = videoRef.current;
    if (!video) return;

    playRequestIdRef.current += 1;
    pendingPlayPromiseRef.current = null;
    lastRequestedActionRef.current = `pause:${reason}`;
    logVideoDebug("debug", "Pause requested", { reason });
    video.pause();
  };

  const requestPlay = (reason) => {
    const video = videoRef.current;
    if (!video) return;

    if (pendingPlayPromiseRef.current) {
      logVideoDebug("debug", "Play request ignored because one is already pending", {
        reason,
      });
      return;
    }

    const requestId = playRequestIdRef.current + 1;
    playRequestIdRef.current = requestId;
    pendingPlayPromiseRef.current = null;
    lastRequestedActionRef.current = `play:${reason}`;
    setError(null);
    logVideoDebug("debug", "Play requested", { reason, requestId });

    let playPromise;
    try {
      playPromise = video.play();
    } catch (err) {
      logVideoDebug("error", "Play request threw before returning a promise", {
        reason,
        requestId,
        errorName: err?.name || null,
        errorMessage: err?.message || String(err),
      });
      if (isMountedRef.current) {
        setError("Failed to play video: " + err.message);
      }
      return;
    }

    pendingPlayPromiseRef.current = playPromise;

    if (!playPromise || typeof playPromise.catch !== "function") {
      pendingPlayPromiseRef.current = null;
      return;
    }

    playPromise
      .then(() => {
        if (playRequestIdRef.current === requestId) {
          logVideoDebug("debug", "Play request resolved", { reason, requestId });
        }
      })
      .catch((err) => {
        const interrupted = isInterruptedPlayError(err);
        const stale = playRequestIdRef.current !== requestId;

        logVideoDebug(interrupted ? "warn" : "error", "Play request rejected", {
          reason,
          requestId,
          stale,
          errorName: err?.name || null,
          errorMessage: err?.message || String(err),
        });

        if (stale || interrupted) {
          if (isMountedRef.current) {
            setIsPlaying(false);
          }
          return;
        }

        if (isMountedRef.current) {
          setError("Failed to play video: " + err.message);
        }
      })
      .finally(() => {
        if (pendingPlayPromiseRef.current === playPromise) {
          pendingPlayPromiseRef.current = null;
        }
      });
  };

  const togglePlay = (reason = "toggle") => {
    const video = videoRef.current;
    if (!video) return;

    if (!video.paused || pendingPlayPromiseRef.current) {
      requestPause(reason);
    } else {
      requestPlay(reason);
    }
  };

  useEffect(() => {
    function handleKeyDown(e) {
      if (e.code === "Space" && !["INPUT", "TEXTAREA", "BUTTON"].includes(e.target.tagName)) {
        e.preventDefault();
        togglePlay("keyboard-space");
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  const toggleFullscreen = () => {
    const el = playerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      el.requestFullscreen().catch(() => {});
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const { currentTime, duration: videoDuration } = videoRef.current;
      if (!Number.isFinite(videoDuration) || videoDuration <= 0) {
        setProgress(0);
        return;
      }
      const nextProgress = (currentTime / videoDuration) * 100;
      setProgress(Number.isFinite(nextProgress) ? nextProgress : 0);

      const now = Date.now();
      if (now - lastSaveTimestampRef.current >= 2000) {
        savePlaybackPosition(currentTime);
        lastSaveTimestampRef.current = now;
      }
    }
  };

  const handleProgressChange = (e) => {
    if (videoRef.current) {
      const videoDuration = videoRef.current.duration;
      if (!Number.isFinite(videoDuration) || videoDuration <= 0) {
        return;
      }
      const time = (e.target.value / 100) * videoDuration;
      if (!Number.isFinite(time)) {
        return;
      }
      videoRef.current.currentTime = time;
      setProgress(Number(e.target.value));
      savePlaybackPosition(time);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleError = (e) => {
    setError(
      "Error loading video: " + (e.target.error?.message || "Unknown error")
    );
  };

  return (
    <div ref={playerRef} className={`video-player ${className || ""}`}>
      {error && (
        <div className="p-4 mb-2 text-sm text-red-500 rounded-lg bg-red-50">
          {error}
        </div>
      )}

      <video
        ref={videoRef}
        className="w-full rounded-lg"
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={(e) => {
          const nextDuration = e.target.duration;
          setDuration(Number.isFinite(nextDuration) ? nextDuration : 0);

          if (
            !hasRestoredPlaybackRef.current &&
            Number.isFinite(nextDuration) &&
            nextDuration > 0
          ) {
            hasRestoredPlaybackRef.current = true;
            const savedPlaybackTime = readSavedPlaybackPosition();

            if (
              Number.isFinite(savedPlaybackTime) &&
              savedPlaybackTime > 0
            ) {
              const maxSeekTime = Math.max(nextDuration - 1, 0);
              const resumeTime = Math.min(savedPlaybackTime, maxSeekTime);

              if (resumeTime > 0) {
                e.target.currentTime = resumeTime;
                const resumedProgress = (resumeTime / nextDuration) * 100;
                setProgress(
                  Number.isFinite(resumedProgress) ? resumedProgress : 0
                );
              }
            }
          }
        }}
        onPlay={() => {
          lastRequestedActionRef.current = "event:play";
          setIsPlaying(true);
          logVideoDebug("debug", "Video play event received");
        }}
        onPause={() => {
          lastRequestedActionRef.current = "event:pause";
          setIsPlaying(false);
          if (videoRef.current) {
            savePlaybackPosition(videoRef.current.currentTime);
          }
          logVideoDebug("debug", "Video pause event received");
        }}
        onEnded={() => {
          lastRequestedActionRef.current = "event:ended";
          setIsPlaying(false);
          clearSavedPlaybackPosition();
          setProgress(100);
          logVideoDebug("debug", "Video ended");
        }}
        onError={handleError}
        autoPlay={autoPlay}
      >
        {/* Primary source with detected MIME type */}
        <source src={src} type={getMimeType(src)} />
        {/* Fallback sources for different formats */}
        {Object.entries(supportedFormats).map(([ext, mimeType]) => {
          const altSrc = src.replace(/\.[^/.]+$/, `.${ext}`);
          if (altSrc !== src) {
            return <source key={ext} src={altSrc} type={mimeType} />;
          }
          return null;
        })}
        Your browser does not support any of the available video formats.
      </video>

      <div className="p-4 mt-2 space-y-2 controls">
        <div className="flex items-center gap-2">
          <button
            onClick={() => togglePlay("controls-button")}
            className="p-2 transition-colors rounded-full hover:bg-gray-200"
          >
            {isPlaying ? (
              <Pause className="w-6 h-6" />
            ) : (
              <Play className="w-6 h-6" />
            )}
          </button>

          <input
            type="range"
            value={progress}
            onChange={handleProgressChange}
            className="grow h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />

          <span className="text-sm text-gray-600">
            {videoRef.current
              ? formatTime(videoRef.current.currentTime)
              : "0:00"}{" "}
            /{duration ? formatTime(duration) : "0:00"}
          </span>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Volume2 className="w-5 h-5" />
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={handleVolumeChange}
            className="w-24 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <SimpleTooltip content="Fullscreen">
            <button
              onClick={toggleFullscreen}
              className="p-1.5 ml-2 transition-colors rounded-full hover:bg-gray-200"
              aria-label="Fullscreen"
            >
              <Maximize className="w-5 h-5" />
            </button>
          </SimpleTooltip>
        </div>
      </div>
    </div>
  );
};

export default VideoPlayer;
